/**
 * Utility functions to manage aria-hidden attributes for accessibility
 * Used to hide content from screen readers when modal dialogs are open
 */

/**
 * Adds aria-hidden="true" to all sibling elements of the modal dialog
 * This prevents screen readers from reading content outside the modal
 * @param {HTMLElement} modalElement - The modal dialog element
 * @returns {Array} - Array of elements that were hidden (for restoration later)
 */
export const hideContentFromScreenReaders = (modalElement) => {
  if (!modalElement) return [];

  const hiddenElements = [];
  const body = document.body;
  
  // Hide all direct children of body except the modal
  Array.from(body.children).forEach((child) => {
    // Skip script, style, and the modal itself
    if (
      child.tagName === 'SCRIPT' ||
      child.tagName === 'STYLE' ||
      child === modalElement ||
      child.contains(modalElement)
    ) {
      return;
    }

    // Only hide if not already hidden
    if (!child.hasAttribute('aria-hidden')) {
      child.setAttribute('aria-hidden', 'true');
      hiddenElements.push(child);
    }
  });

  return hiddenElements;
};

/**
 * Hides specific elements from screen readers
 * @param {Array<string>} selectors - Array of CSS selectors for elements to hide
 * @returns {Array} - Array of elements that were hidden (for restoration later)
 */
export const hideElementsBySelectors = (selectors = []) => {
  const hiddenElements = [];

  selectors.forEach((selector) => {
    const elements = document.querySelectorAll(selector);
    elements.forEach((element) => {
      if (!element.hasAttribute('aria-hidden')) {
        element.setAttribute('aria-hidden', 'true');
        element.setAttribute('data-aria-hidden-by-modal', 'true');
        hiddenElements.push(element);
      }
    });
  });

  return hiddenElements;
};

/**
 * Removes aria-hidden from previously hidden elements
 * @param {Array<HTMLElement>} elements - Array of elements to restore
 */
export const restoreContentForScreenReaders = (elements = []) => {
  elements.forEach((element) => {
    if (element && element.hasAttribute('aria-hidden')) {
      element.removeAttribute('aria-hidden');
      element.removeAttribute('data-aria-hidden-by-modal');
    }
  });
};

/**
 * Manages focus trap within a modal dialog
 * @param {HTMLElement} modalElement - The modal dialog element
 * @param {Event} event - The keyboard event
 * @returns {boolean} - Whether the event was handled
 */
export const handleFocusTrap = (modalElement, event) => {
  if (!modalElement || event.key !== 'Tab') return false;

  const focusableElements = Array.from(
    modalElement.querySelectorAll(
      'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
    )
  ).filter((el) => {
    const style = getComputedStyle(el);
    return (
      el.offsetParent !== null &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      !el.hasAttribute('hidden')
    );
  });

  if (focusableElements.length === 0) return false;

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  const activeElement = document.activeElement;

  if (event.shiftKey) {
    // Shift+Tab: if on first element, go to last
    if (activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
      return true;
    }
  } else {
    // Tab: if on last element, go to first
    if (activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
      return true;
    }
  }

  return false;
};

/**
 * Focuses the first focusable element in the modal
 * @param {HTMLElement} modalElement - The modal dialog element
 */
export const focusFirstElement = (modalElement) => {
  if (!modalElement) return;

  const focusableElements = modalElement.querySelectorAll(
    'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
  );

  if (focusableElements.length > 0) {
    focusableElements[0].focus();
  } else {
    modalElement.focus();
  }
};
